import axios from "axios";

/**
 * Downloads a generated export file.
 *
 * The export service no longer serves the generated files as public static
 * assets — anyone could download another user's export by guessing
 * "List_Data_Organisasi_<tanggal>.xlsx" — so the file has to be fetched with
 * the user's token instead of opened in a new tab.
 *
 * Takes the `meta` object the export endpoints already return
 * ({urlPath, fileName}). Resolves when the file has been handed to the
 * browser, rejects with a readable Error so the caller's existing
 * Swal error handler keeps working.
 */
export function downloadExportFile(meta) {
  const url = meta && meta.urlPath;

  if (!url) {
    return Promise.reject(new Error("URL file export tidak ditemukan"));
  }

  return axios({
    url: url,
    method: "GET",
    responseType: "blob",
    headers: {
      Authorization: "Bearer " + localStorage.getItem("token")
    }
  })
    .then(resp => {
      saveBlob(resp.data, resolveFileName(resp, meta, url));
    })
    .catch(err => {
      return readableError(err).then(message => {
        return Promise.reject(new Error(message));
      });
    });
}

// Content-Disposition is the authoritative name; meta.fileName covers the case
// where the header is not exposed to the browser, and the URL is the last resort.
export function resolveFileName(resp, meta, url) {
  const disposition =
    (resp && resp.headers && resp.headers["content-disposition"]) || "";

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch (e) {
      // fall through to the plain filename
    }
  }

  // skip past any filename*= parameter so its value cannot be matched here
  const plain = disposition.replace(/filename\*=[^;]*/i, '').match(/filename="?([^";]+)"?/i);
  if (plain) return plain[1].trim();

  if (meta && meta.fileName) return meta.fileName;

  const fromUrl = String(url || "")
    .split("?")[0]
    .split("/")
    .pop();

  return fromUrl || "export";
}

function saveBlob(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  // Firefox only follows the click when the anchor is in the document.
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
}

// With responseType "blob" an error body arrives as a Blob, so the usual
// err.response.data.error.message is not readable without unpacking it first.
function readableError(err) {
  const status = err && err.response && err.response.status;
  const data = err && err.response && err.response.data;

  const fallback = () => {
    if (status === 401 || status === 403) {
      return "Sesi Anda telah berakhir, silakan login kembali";
    }

    if (status === 404) {
      return "File export tidak ditemukan atau sudah kedaluwarsa";
    }

    return (err && err.message) || "Gagal mengunduh file export";
  };

  if (!data || typeof data.slice !== "function") {
    return Promise.resolve(fallback());
  }

  return blobToText(data)
    .then(text => {
      const parsed = JSON.parse(text);
      const message = parsed && parsed.error && parsed.error.message;

      return message || fallback();
    })
    .catch(() => fallback());
}

// FileReader rather than Blob.text(), which is unavailable in older browsers.
function blobToText(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}
